import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { getBlogPost } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import RelatedContent from '../components/RelatedContent';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImage = (img) => {
  if (!img) return null;
  if (img.startsWith('http')) return img;
  return `${BACKEND_URL}${img}`;
};

const useReveal = (deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const nodes = el.querySelectorAll('.rv:not(.is-visible)');
    // Immediately reveal elements already inside the viewport on mount so
    // above-the-fold content does not stay invisible (IO can fire late).
    const vh = window.innerHeight || 0;
    nodes.forEach((n) => {
      const r = n.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) n.classList.add('is-visible');
    });
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv:not(.is-visible)').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

// Editorial Rich Section — used for blog post body
const RichSection = ({ section, language, getTranslated }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const title = getTranslated(section, 'title');
  const content = getTranslated(section, 'content');

  const renderContent = () => {
    switch (section.type) {
      case 'text':
        return (
          <div>
            {title && (
              <h2
                className="text-editorial-charcoal font-light mt-12 mb-7"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)',
                  lineHeight: '1.22',
                }}
              >
                {title}
              </h2>
            )}
            {content && (
              <div
                className="prose-luxury text-editorial-charcoal-soft font-light"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '1.02rem',
                  lineHeight: '1.95',
                }}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </div>
        );

      case 'text_with_image': {
        const hasImage = section.images && section.images.length > 0;
        const imagePosition = section.imagePosition || 'left';
        const imgSrc = hasImage ? resolveImage(section.images[0]) : null;

        if (imagePosition === 'full') {
          return (
            <div>
              {imgSrc && (
                <div className="my-12 aspect-[16/9] overflow-hidden bg-editorial-sand">
                  <img
                    src={imgSrc}
                    alt={title || ''}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => setLightboxImage(imgSrc)}
                  />
                </div>
              )}
              {title && (
                <h2
                  className="text-editorial-charcoal font-light mt-12 mb-7"
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)',
                    lineHeight: '1.22',
                  }}
                >
                  {title}
                </h2>
              )}
              {content && (
                <div
                  className="prose-luxury text-editorial-charcoal-soft font-light"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '1.02rem',
                    lineHeight: '1.95',
                  }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
            </div>
          );
        }

        return (
          <div
            className={`grid gap-10 lg:gap-14 items-center grid-cols-1 md:grid-cols-2 ${
              imagePosition === 'right' ? 'md:[&>*:first-child]:order-2' : ''
            }`}
          >
            {imgSrc && (
              <div className="aspect-[4/5] overflow-hidden bg-editorial-sand">
                <img
                  src={imgSrc}
                  alt={title || ''}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setLightboxImage(imgSrc)}
                />
              </div>
            )}
            <div>
              {title && (
                <h2
                  className="text-editorial-charcoal font-light mb-6"
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: 'clamp(1.6rem, 2.4vw, 2.1rem)',
                    lineHeight: '1.22',
                  }}
                >
                  {title}
                </h2>
              )}
              {content && (
                <div
                  className="prose-luxury text-editorial-charcoal-soft font-light"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.98rem',
                    lineHeight: '1.92',
                  }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
            </div>
          </div>
        );
      }

      case 'gallery':
        return (
          <div>
            {title && (
              <h2
                className="text-editorial-charcoal font-light mt-12 mb-7"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)',
                  lineHeight: '1.22',
                }}
              >
                {title}
              </h2>
            )}
            {section.images && section.images.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-editorial-sand-deep">
                {section.images.map((img, idx) => {
                  const src = resolveImage(img);
                  return (
                    <div
                      key={idx}
                      className="aspect-square overflow-hidden bg-editorial-sand"
                    >
                      <img
                        src={src}
                        alt={`${title || 'Gallery'} ${idx + 1}`}
                        className="w-full h-full object-cover cursor-pointer transition-transform duration-700 hover:scale-105"
                        onClick={() => setLightboxImage(src)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="my-12 md:my-16">{renderContent()}</div>
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-editorial-bone hover:text-editorial-bronze-lt transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-7 h-7" />
          </button>
          <img
            src={lightboxImage}
            alt="Lightbox"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

const BlogPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, localizedPath } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headerFooterData, setHeaderFooterData] = useState(null);

  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) return obj[`${field}_en`];
    return obj[field] || '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const postData = await getBlogPost(id);
        setPost(postData);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/home-content`);
        const homeData = await response.json();
        setHeaderFooterData(homeData.headerFooter);
      } catch (error) {
        console.error('Error loading blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const ref = useReveal([post]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-editorial-mineral">
        <p
          className="text-editorial-muted italic"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem' }}
        >
          {language === 'pt' ? 'A carregar…' : 'Loading…'}
        </p>
      </div>
    );
  }

  if (!post) {
    return (
      <>
        <Header
          companyName={headerFooterData?.companyName}
          logo={headerFooterData?.logo}
        />
        <main className="min-h-screen flex flex-col items-center justify-center bg-editorial-mineral px-6">
          <Eyebrow>{language === 'pt' ? 'Erro 404' : 'Error 404'}</Eyebrow>
          <h1
            className="text-editorial-charcoal font-light mt-6 mb-9 text-center"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              lineHeight: '1.1',
            }}
          >
            {language === 'pt' ? 'Ensaio não encontrado' : 'Essay not found'}
          </h1>
          <Link
            to={localizedPath('/journal')}
            className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 py-4 px-10"
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '0.65rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 500,
              borderRadius: 0,
            }}
          >
            {language === 'pt' ? 'Voltar ao Journal' : 'Back to Journal'}
          </Link>
        </main>
      </>
    );
  }

  const title = getTranslated(post, 'title');
  const content = getTranslated(post, 'content');
  const excerpt = getTranslated(post, 'excerpt');
  const category = getTranslated(post, 'category');
  const heroImage = resolveImage(post.image);

  const formattedDate = post.date || post.created_at
    ? new Date(post.date || post.created_at).toLocaleDateString(
        language === 'pt' ? 'pt-PT' : 'en-US',
        { year: 'numeric', month: 'long', day: 'numeric' }
      )
    : null;

  return (
    <>
      <SEO
        title={`${title} — Your Broker Portugal`}
        description={excerpt}
        type="article"
        url={`https://yourbrokerportugal.com/journal/${post.slug || post.id}`}
      />
      {post.slug && (
        <link
          rel="canonical"
          href={`https://yourbrokerportugal.com/journal/${post.slug}`}
        />
      )}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: title,
          description: excerpt,
          image: heroImage,
          datePublished: post.date || post.created_at,
          dateModified: post.updated_at || post.date || post.created_at,
          author: { '@type': 'Organization', name: 'Your Broker Portugal' },
          publisher: {
            '@type': 'Organization',
            name: 'Your Broker Portugal',
            logo: {
              '@type': 'ImageObject',
              url: 'https://yourbrokerportugal.com/logo-ybp.png',
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://yourbrokerportugal.com/journal/${post.slug || post.id}`,
          },
          articleBody: content,
          articleSection: category,
          inLanguage: language === 'pt' ? 'pt-PT' : 'en-US',
        })}
      </script>

      <style>{`
        body { background: #f7f4ef; }
        @keyframes rvFadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .rv { animation: rvFadeUp .95s ease both; }
        .rv:nth-child(2) { animation-delay: .08s; }
        .rv:nth-child(3) { animation-delay: .16s; }
        .rv:nth-child(4) { animation-delay: .24s; }

        /* Editorial body type for HTML content */
        .prose-luxury p { margin-bottom: 1.65em; }
        .prose-luxury h2, .prose-luxury h3 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          color: #1c1c1a;
          line-height: 1.22;
          margin-top: 2.2em;
          margin-bottom: 0.9em;
        }
        .prose-luxury h2 { font-size: clamp(1.55rem, 2.4vw, 2.1rem); }
        .prose-luxury h3 { font-size: clamp(1.3rem, 2vw, 1.65rem); }
        .prose-luxury a {
          color: #b8955a;
          border-bottom: 1px solid rgba(184,149,90,0.4);
          transition: border-color .25s ease;
        }
        .prose-luxury a:hover { border-bottom-color: #b8955a; }
        .prose-luxury strong { color: #1c1c1a; font-weight: 500; }
        .prose-luxury ul, .prose-luxury ol { margin: 1em 0 1.65em 1.4em; }
        .prose-luxury ul { list-style: none; padding-left: 0; }
        .prose-luxury ul li { position: relative; padding-left: 1.4em; margin-bottom: 0.6em; }
        .prose-luxury ul li::before {
          content: '—';
          position: absolute;
          left: 0;
          color: #b8955a;
        }
        .prose-luxury ol { list-style: decimal; padding-left: 1.4em; }
        .prose-luxury blockquote {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-size: 1.4rem;
          line-height: 1.55;
          color: #2a2a28;
          border-left: 2px solid #b8955a;
          padding-left: 1.6em;
          margin: 2.2em 0;
          max-width: 32em;
        }
        .prose-luxury img {
          width: 100%;
          height: auto;
          margin: 1.8em 0;
        }
        .prose-luxury hr {
          border: 0;
          border-top: 1px solid rgba(28,28,26,0.12);
          margin: 3em auto;
          max-width: 4rem;
        }
      `}</style>

      {headerFooterData && (
        <Header
          companyName={headerFooterData.companyName}
          logo={headerFooterData.logo}
        />
      )}

      <main ref={ref}>
        {/* ARTICLE HEADER */}
        <section className="pt-28 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-3xl mx-auto">
            <Link
              to={localizedPath('/journal')}
              className="rv inline-flex items-center gap-2 text-editorial-muted/70 hover:text-editorial-bronze transition-colors mb-10"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.62rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
              data-testid="post-back-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {language === 'pt' ? 'Voltar ao Journal' : 'Back to Journal'}
            </Link>

            {category && (
              <div className="rv">
                <Eyebrow>{category}</Eyebrow>
              </div>
            )}

            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-9"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: 'clamp(2.2rem, 4.2vw, 3.8rem)',
                lineHeight: '1.08',
              }}
              data-testid="post-title"
            >
              {title}
            </h1>

            {formattedDate && (
              <p
                className="rv text-editorial-muted/70 mb-2"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                {formattedDate} · Your Broker Portugal
              </p>
            )}
          </div>
        </section>

        {/* HERO IMAGE — full bleed, no rounding */}
        {heroImage && (
          <section className="rv px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral pb-12 md:pb-16">
            <div className="max-w-6xl mx-auto">
              <div className="aspect-[16/9] overflow-hidden bg-editorial-sand">
                <img
                  src={heroImage}
                  alt={title}
                  className="w-full h-full object-cover"
                  style={{ filter: 'contrast(1.02)' }}
                />
              </div>
            </div>
          </section>
        )}

        {/* BODY */}
        <article className="px-6 md:px-10 lg:px-16 xl:px-24 pb-20 md:pb-28 bg-editorial-mineral">
          <div className="max-w-3xl mx-auto">
            {/* Lead / excerpt */}
            {excerpt && (
              <div
                className="rv text-editorial-charcoal font-light mb-12 pb-12 border-b"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(1.35rem, 1.8vw, 1.65rem)',
                  lineHeight: '1.55',
                  borderColor: 'rgba(28,28,26,0.10)',
                }}
              >
                {excerpt}
              </div>
            )}

            {/* Rich sections OR fallback content */}
            {post.sections && post.sections.length > 0 ? (
              <div>
                {post.sections
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((section) => (
                    <RichSection
                      key={section.id}
                      section={section}
                      language={language}
                      getTranslated={getTranslated}
                    />
                  ))}
              </div>
            ) : content ? (
              <div
                className="prose-luxury text-editorial-charcoal-soft font-light whitespace-pre-line"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '1.02rem',
                  lineHeight: '1.95',
                }}
              >
                {content}
              </div>
            ) : null}
          </div>
        </article>

        {/* RELATED CONTENT */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-16 md:py-20 bg-editorial-sand/30">
          <div className="max-w-6xl mx-auto">
            <RelatedContent type="blog" slug={post.slug || id} />
          </div>
        </section>

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-32 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="rv">
              <Eyebrow dark>
                {language === 'pt' ? 'Conversa privada' : 'Private conversation'}
              </Eyebrow>
              <h2
                className="text-editorial-bone font-light mt-7 mb-6"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 'clamp(2rem, 3.6vw, 3.2rem)',
                  lineHeight: '1.08',
                }}
              >
                {language === 'pt'
                  ? 'Da leitura'
                  : 'From reading'}{' '}
                <em className="italic text-editorial-bronze-lt">
                  {language === 'pt' ? 'à decisão.' : 'to decision.'}
                </em>
              </h2>
              <p
                className="text-editorial-bone/60 font-light max-w-lg"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.95rem',
                  lineHeight: '1.95',
                }}
              >
                {language === 'pt'
                  ? 'Se este tema toca o seu projecto em Portugal, conversemos. Trinta minutos, sem agenda comercial.'
                  : 'If this topic touches your project in Portugal, let us speak. Thirty minutes, free of commercial agenda.'}
              </p>
            </div>
            <div className="rv flex flex-col gap-5 items-start">
              <a
                href={buildWhatsAppUrl('journal-post', language)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  borderRadius: 0,
                }}
                data-testid="post-cta-whatsapp"
              >
                {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
              </a>
              <Link
                to={localizedPath('/contact')}
                className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.65rem',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
                data-testid="post-cta-contact"
              >
                {language === 'pt' ? 'Iniciar conversa privada' : 'Begin a private conversation'}
              </Link>
            </div>
          </div>
        </section>
      </main>

      {headerFooterData && <Footer data={headerFooterData} />}
    </>
  );
};

export default BlogPostPage;
